import { useEffect, useRef, useCallback } from 'react';
import useAuthStore from '../stores/authStore';

const EVENTS = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;
const THROTTLE_MS = 30_000;  // 30 秒內只重設一次 timer，避免高頻觸發

// 趨勢獨立看板為被動顯示頁（無滑鼠/鍵盤互動、常駐播放），一律不套用閒置自動登出：
// 否則它會用預設 30 分逾時、且清掉共享 cookie，把主視窗也一起登出。
// session 由 useTokenRefresh 定時續期維持，分頁不會掉線。
function isStandaloneBoard(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/history/standalone');
}

/**
 * 閒置自動登出 hook — 監聽使用者互動事件，超過設定時間後自動登出。
 * 由 App.tsx 中呼叫一次即可。
 */
export default function useIdleTimer() {
  const token = useAuthStore((s) => s.token);
  const idleTimeoutMinutes = useAuthStore((s) => s.idleTimeoutMinutes);
  const logout = useAuthStore((s) => s.logout);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetRef = useRef<number>(0);

  const resetTimer = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current < THROTTLE_MS) return;
    lastResetRef.current = now;

    if (timerRef.current) clearTimeout(timerRef.current);
    if (!token || idleTimeoutMinutes <= 0 || isStandaloneBoard()) return;

    timerRef.current = setTimeout(() => {
      logout('idle');
    }, idleTimeoutMinutes * 60 * 1000);
  }, [token, idleTimeoutMinutes, logout]);

  useEffect(() => {
    if (!token || idleTimeoutMinutes <= 0 || isStandaloneBoard()) return;

    // 首次啟動 timer
    lastResetRef.current = 0;  // 確保首次不被 throttle
    resetTimer();

    const handler = () => resetTimer();
    EVENTS.forEach((e) => window.addEventListener(e, handler, { passive: true }));

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token, idleTimeoutMinutes, resetTimer]);
}
